import { addDays, format, formatISO, startOfWeek } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';

import { MONDAY, MONDAY_OF_WEEK } from '@/util/constants';
import { getInitialStartDate, updateUrlParams } from '@/util/dashboard';

// Owns the visible 7-day window for the v2 calendar: Monday-anchored start
// date, prev/next navigation, a one-click "Today" jump, and `?sd=` URL sync
// (deep links + refresh land on the same week). Mirrors the legacy
// dashboard's week nav (src/util/dashboard.ts) so behaviour is identical.
const noopLog = () => {};

// "Jun 22 – 28, 2026" within a month, "Jun 29 – Jul 5, 2026" across months.
const formatRange = (start: Date): string => {
  const end = addDays(start, 6);
  const year = format(end, 'yyyy');
  if (format(start, 'MMM') === format(end, 'MMM')) {
    return `${format(start, 'MMM d')} – ${format(end, 'd')}, ${year}`;
  }
  return `${format(start, 'MMM d')} – ${format(end, 'MMM d')}, ${year}`;
};

const useWeekNav = (agencyId: number) => {
  const [startDate, setStartDate] = useState<Date>(() =>
    getInitialStartDate(noopLog, agencyId),
  );

  const startDateISO = useMemo(
    () => formatISO(startDate, { representation: 'date' }),
    [startDate],
  );

  const commit = useCallback((next: Date) => {
    const monday = startOfWeek(next, { weekStartsOn: MONDAY });
    setStartDate(monday);
    updateUrlParams({ sd: formatISO(monday, { representation: 'date' }) });
  }, []);

  const goPrev = useCallback(
    () => commit(addDays(startDate, -7)),
    [commit, startDate],
  );
  const goNext = useCallback(
    () => commit(addDays(startDate, 7)),
    [commit, startDate],
  );
  const goToday = useCallback(() => commit(MONDAY_OF_WEEK), [commit]);

  return {
    startDate,
    startDateISO,
    rangeLabel: formatRange(startDate),
    goPrev,
    goNext,
    goToday,
  };
};

export default useWeekNav;
